import React, { Fragment, useState } from 'react'
import * as yup from 'yup'
import fetch from 'cross-fetch'
import Helmet from 'react-helmet'
import Layout from 'components/Layout'
import Form from 'components/Form'
import Container from 'components/Container'
import newPatientForm from 'static/documents/new_patient_form.pdf'
import Field from 'components/Form/Field'
import composeHandlers from 'utils/composeHandlers'

const OcsInput = ({
  name,
  label,
  type = 'text',
  placeholder,
  multiline,
  onChange,
  onBlur,
  formatValue
}) => (
  <Field
    name={name}
    render={({
      onBlur: fieldOnBlur,
      onChange: fieldOnChange,
      renderError,
      setValue,
      valid,
      error,
      form,
      ...field
    }) => (
      <div className="ocs-form__field">
        <label className="ocs-form__label" htmlFor={name}>
          {label}
        </label>
        {multiline ? (
          <textarea
            {...field}
            id={name}
            className={`ocs-form__textarea${
              !valid ? ' ocs-form__textarea--error' : ''
            }`}
            placeholder={placeholder}
            onBlur={composeHandlers(fieldOnBlur, onBlur)}
            onChange={e => {
              onChange && onChange(e)
              formatValue
                ? setValue(formatValue(e.target.value))
                : fieldOnChange(e)
            }}
          />
        ) : (
          <input
            {...field}
            id={name}
            type={type}
            className={`ocs-form__input${
              !valid ? ' ocs-form__input--error' : ''
            }`}
            placeholder={placeholder}
            onBlur={composeHandlers(fieldOnBlur, onBlur)}
            onChange={e => {
              onChange && onChange(e)
              formatValue
                ? setValue(formatValue(e.target.value))
                : fieldOnChange(e)
            }}
          />
        )}
        {error && <p className="ocs-form__error">{error}</p>}
      </div>
    )}
  />
)

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Email must be a valid email address')
    .required("Email can't be blank"),
  first_name: yup.string().required("First name can't be blank"),
  last_name: yup.string().required("Last name can't be blank"),
  phone: yup
    .number()
    .required("Phone number can't be blank")
    .positive()
})

const Appointments = () => {
  const [sent, setSent] = useState(false)

  return (
    <Layout>
      <Helmet title="Oregon City Smiles - Appointments" />

      {/* Hero section */}
      <div className="ocs-form-hero">
        <Container>
          <h1 className="ocs-form-hero__title">Request an Appointment</h1>
          <p className="ocs-form-hero__description">
            Fill out the form below and our staff will contact you to schedule
            your visit. We look forward to seeing you.
          </p>
        </Container>
      </div>

      {/* Form card */}
      <Container>
        <div className="ocs-form" style={{ paddingBottom: 64 }}>
          <div className="ocs-form__card">
            {sent ? (
              <div className="ocs-form__success">
                <div className="ocs-form__success-icon">
                  <svg width="24" height="24" viewBox="0 0 20 20" fill="white">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h2 className="ocs-form__success-title">Request Received</h2>
                <p className="ocs-form__success-text">
                  Thank you for your appointment request. Our team will reach
                  out to you shortly to confirm your appointment.
                </p>
              </div>
            ) : (
              <Fragment>
                <h2 className="ocs-form__title">Contact Information</h2>
                <p className="ocs-form__subtitle">
                  Office hours: Mon&ndash;Thu 8:00 AM &ndash; 5:00 PM, Fri 8:00
                  AM &ndash; 1:00 PM
                </p>

                <Form
                  validationSchema={schema}
                  onSubmit={params =>
                    fetch(
                      'https://home.peterjlee.net/oregon-city-smiles/request_appointment',
                      // 'http://localhost:4002/oregon-city-smiles/request_appointment',
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(params)
                      }
                    ).then(() => {
                      setSent(true)
                    })
                  }
                >
                  {/* First + Last name in a 2-col grid */}
                  <div className="ocs-form__grid">
                    <OcsInput
                      name="first_name"
                      label="First name"
                      placeholder="Jane"
                    />
                    <OcsInput
                      name="last_name"
                      label="Last name"
                      placeholder="Doe"
                    />
                  </div>

                  <OcsInput
                    name="email"
                    label="Email"
                    type="email"
                    placeholder="jane@example.com"
                  />

                  <OcsInput
                    name="phone"
                    label="Phone"
                    type="tel"
                    placeholder="(503) 555-0100"
                  />

                  <OcsInput
                    name="comments"
                    label="Additional comments"
                    placeholder="Preferred dates, reason for visit, etc."
                    multiline
                  />

                  <div className="ocs-form__footer">
                    <button type="submit" className="ocs-form__submit">
                      Request Appointment
                    </button>
                  </div>

                  <p className="ocs-form__disclaimer">
                    First time patient?{' '}
                    <a
                      href={newPatientForm}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download the new patient registration form
                    </a>{' '}
                    to save time at your first visit.
                  </p>
                </Form>
              </Fragment>
            )}
          </div>
        </div>
      </Container>
    </Layout>
  )
}

export default Appointments
